/* ManageConnectionsRenderer.js */

"use strict";
/* global Slick */

const _ = require("lodash");
const formatter = require("./cellFormaters");
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const editDialog = require("./veil/addEditConnection/AddEditConnectionComponent");

/**
 * class ManageConnectionsRenderer
 *
 * @listens ManageConnectionsRenderer#event:feedUpdated - Triggered when the service connections feed is updated.
 * @listens ManageConnectionsRenderer#event:OrganizationChanged - Triggered when the active organization changes.
 * @listens ManageConnectionsRenderer#event:UserClick - Triggered when the "Add Connection" button is clicked.
 * @listens window#resize - Listens for window resize events to adjust the table size.
 * @listens document#click - Listens for user interactions on the document (e.g., button clicks in the table).
 */
class ManageConnectionsRenderer {
    /**
     * @param {ManageConnectionsComponent} model - Data model from component.
     *
     */
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("manage-connections-gl");
        this._rootElem.component = this._model;

        this._headerElem = document.createElement("h1");
        this._headerElem.innerText = "Connections";
        this._rootElem.appendChild(this._headerElem);

        this._noDataMessage = document.createElement("span");
        this._noDataMessage.innerText = "No Connections Available";
        this._noDataPlaceHolder = this._createNoDataPlaceholder(this._noDataMessage);

        this._addNewConnectionButton = new ButtonControl({
            label: "Add Connection +",
            active: true,
            class: "add-new-connection",
        });

        this.buttonContainer = document.createElement("div");
        this.buttonContainer.classList.add("add-new-row");
        this.buttonContainer.appendChild(this._addNewConnectionButton.renderer.getRootElement());

        this._clickHandler = _.debounce(this._clickHandler.bind(this), 500, {
            leading: true,
            trailing: false,
        });
        this._fitTableSize = this._fitTableSize.bind(this);

        this._registerListeners();

        this._tableOptions = {
            editable: true,
            enableAddRow: false,
            enableCellNavigation: true,
            enableColumnReorder: false,
            forceFitColumns: true,
            enableAsyncPostRender: true,
            asyncEditorLoading: false,
            rowHeight: 45,
            autoHeight: true,
        };

        this._connectionContainer = document.createElement("connection-container");
        this._slickTableContainer = document.createElement("user-connections-table");
        this._slickWrapper = document.createElement("user-connections-table-wrapper");
        this._slickWrapper.appendChild(this._slickTableContainer);
        this._slickTable = undefined;
        this._connectionContainer.appendChild(this._slickWrapper);

        this._rootElem.appendChild(this._slickWrapper);

        this._columns = [
            {
                id: "shared",
                width: 80,
                name: "Shared",
                toolTip: "Share this connection with child organizations",
                field: "shared",
                resizable: false,
                cssClass: "centered",
                headerCssClass: "centered",
                formatter: formatter.sharedCheckboxFormatter,
            },
            {
                id: "name",
                width: 200,
                name: "Name",
                toolTip: "Name of the connection",
                field: "name",
                resizable: true,
                formatter: formatter.nameFormatter,
            },
            {
                id: "description",
                width: 200,
                name: "Description",
                toolTip: "Description of the connection",
                field: "description",
                resizable: true,
                formatter: formatter.descriptionFormatter,
            },
            {
                id: "connectionType",
                width: 200,
                name: "Connection Type",
                toolTip: "Connection Type",
                field: "serviceConnectionTypeDisplayName",
                resizable: true,
                formatter: formatter.connectionTypeNameFormatter,
            },
            {
                id: "actions",
                width: 170,
                name: "Actions",
                toolTip: "View, edit, and remove connections",
                resizable: false,
                cssClass: "actions",
                headerCssClass: "centered",
                formatter: formatter.actionButtonsFormatter(this._model),
            },
        ];
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        this._unregisterListeners();

        let promArr = [];

        if (this._slickTable && this._slickTable.destroy) {
            this._slickTable.destroy();
        }

        return Promise.all(promArr).then(() => {
            this._model = undefined;
            this._headerElem.remove();
            this._headerElem = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._noDataMessage.remove();
            this._noDataMessage = undefined;

            this._noDataPlaceHolder = undefined;
            this.buttonContainer = undefined;
            this._clickHandler = undefined;
            this._fitTableSize = undefined;
            this._tableOptions = undefined;
            this._connectionContainer = undefined;
            this._slickTableContainer = undefined;
            this._slickTable = undefined;
            this._columns = undefined;
        });
    }

    _registerListeners() {
        this._addNewConnectionButton.on("UserClick", () => this._addNewConnectionButtonClick());
        document.addEventListener("click", this._clickHandler);
        this._model.on("feedUpdated", () => this._onFeedUpdated());
        this._model.on("OrganizationChanged", async () => await this._onOrganizationChanged());
        window.addEventListener("resize", this._fitTableSize);
    }

    _unregisterListeners() {
        this._addNewConnectionButton.removeAllListeners("UserClick");
        document.removeEventListener("click", this._clickHandler);
        this._model.removeListener("feedUpdated", this._onFeedUpdated);
        this._model.removeListener("OrganizationChanged", this._onOrganizationChanged);
        window.removeEventListener("resize", this._fitTableSize);
    }

    _createNoDataPlaceholder(noDataMessage) {
        const noDataPlaceholder = document.createElement("div");
        noDataPlaceholder.classList.add("no-data-placeholder");
        noDataPlaceholder.appendChild(noDataMessage);
        return noDataPlaceholder;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this.model) {
            return this.model.whenModelIsReady().then(() => {
                if (!this._slickTableDataView) {
                    this._slickTableDataView = new Slick.Data.DataView();
                }
                if (_.isEmpty(this._slickTable)) {
                    this._slickTableDataView.setItems(this.model.slickData);
                    this.sortTable();
                    this.iterateGridRows();

                    this._slickTable = new Slick.Grid(
                        this._slickTableContainer,
                        this._slickTableDataView,
                        this._columns,
                        this._tableOptions
                    );
                } else {
                    this.setSlickGridTableData(this.model.slickData);
                }
                if (!this._slickTableContainer.contains(this._noDataPlaceHolder)) {
                    this._slickTableContainer.appendChild(this._noDataPlaceHolder);
                }
                this.checkIsEmpty();
                this._slickTableContainer.appendChild(this.buttonContainer);
            });
        }
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    setSlickGridTableData(newData) {
        if (newData) {
            //we expect a non-empty array to process
            return new Promise((resolve) => {
                this._slickTableDataView.setItems(newData);
                this.sortTable();
                this._slickTable.invalidate();
                this._slickTable.render();
                this.checkIsEmpty();
                this._fitTableSize(); // fix windows os slickgrid resize issue
                return resolve();
            });
        }
        return Promise.resolve();
    }

    _fitTableSize() {
        if (this._slickTable) {
            this._slickTable.resizeCanvas();
        }
    }

    _openConnectionEditor(serviceConnectionEntity, isInherited = false, isSystemManaged = false) {
        const componentOptions = {
            manageConnectionComponent: this.model,
            entityDef: this.model._ServiceConnectionEntityDef,
            userFeedback: this.model.userFeedback,
            activeOrgId: this.model.activeOrgId,
            typeEntities: this.model._serviceConnectionTypes,
            uiUtils: this.model._uiUtils,
            isInherited: isInherited,
            isSystemManaged: isSystemManaged,
        };

        if (!_.isEmpty(serviceConnectionEntity)) {
            componentOptions.connectionEntity = serviceConnectionEntity;
        }

        const component = new editDialog(componentOptions);
        this._modelDialogOpen(component);
    }

    _modelDialogOpen(component) {
        this.model.userFeedback.displayCustom(component, {notDismissable: false}).catch((error) => {
            if (error && !error.userRejected) {
                this._logger.warn(
                    {error: error},
                    "ManageConnectionsRenderer._openConnectionEditor() Error in _openConnectionEditor"
                );
            }
        });
    }

    sortTable(sortByField) {
        sortByField = sortByField || "name";
        if (this._slickTableDataView) {
            this._slickTableDataView.sort(this.getSortingComparator(sortByField));
        }
    }

    getSortingComparator(sortField = "name") {
        const fieldCompare = (aItem, bItem) => {
            const a = aItem[sortField];
            const b = bItem[sortField];
            if (_.isNumber(a) && _.isNumber(b)) {
                return a - b;
            }
            return String(a).localeCompare(String(b));
        };
        const isInherited = (item) => Boolean(item.isInherited);
        const inheritedFirst = (a, b) =>
            isInherited(a) === isInherited(b) ? 0 : isInherited(a) ? -1 : 1;
        return (a, b) => inheritedFirst(a, b) || fieldCompare(a, b);
    }

    iterateGridRows() {
        this._slickTableDataView.getItemMetadata = ((original) => (row) => {
            const item = this._slickTableDataView.getItem(row);
            let ret = original(row);
            if (item && item.isInherited) {
                ret = ret || {};
                ret.cssClasses = (ret.cssClasses || "") + " readonly-row";
            }
            return ret;
        })(this._slickTableDataView.getItemMetadata);
    }

    _editConnection(data) {
        this._model._retrieveConnection(data.id).then((serviceConnectionEntity) => {
            this._openConnectionEditor(serviceConnectionEntity, false, data.isSystemManaged);
        });
    }

    _viewConnection(data) {
        const entity = _.get(this._model.serviceConnectionsFeed, `cache.${data.id}`);
        if (entity) {
            this._openConnectionEditor(entity, true);
        }
    }

    _deleteConnection(data) {
        const deleteMessage = `Are you sure you want to delete the connection "${data.name}"?`;
        return this.model.userFeedback
            .displayConfirmation(deleteMessage, {
                title: "Delete",
                confirmDetails: {
                    text: "Confirm",
                    type: "destructive",
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text",
                },
                notDismissable: true,
                dontHide: true,
            })
            .then(() => {
                const loadingVeil = () =>
                    this.model.userFeedback.displayLoading({
                        message: "Deleting the connection... This could take a moment",
                    });
                return Promise.all([loadingVeil(), this.model.deleteConnection(data.id)]);
            })
            .catch((error) => {
                if (error && !error.userRejected) {
                    console.log("Error in Delete: _deleteConnection()", error);
                    if (error.errorMsg && error.errorMsg.search("referenced by") >= 0) {
                        error.errorMsg =
                            "This connection is currently in use and cannot be deleted. Please remove its association before attempting to delete it.";
                    }
                    return this._model && this.model.userFeedback.displayError(error);
                }
            })
            .then(() => this._model && this.model.userFeedback.hideDisplayed());
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _addNewConnectionButtonClick() {
        this._openConnectionEditor(null);
    }

    _clickHandler(e) {
        //if we click delete column
        const target = e.target;
        if (target.getAttribute("role") === "button" && !target.getAttribute("disabled")) {
            const row = target.getAttribute("row");
            const data = this._slickTableDataView.getItemByIdx(row);

            switch (target.getAttribute("action")) {
                case "edit":
                    this._editConnection(data);
                    break;
                case "view":
                    this._viewConnection(data);
                    break;
                case "delete":
                    this._deleteConnection(data);
                    break;
                default:
                    break;
            }
        }
        e.stopPropagation();
    }

    _onFeedUpdated() {
        if (this._model) {
            this.setSlickGridTableData(this._model.slickData);
        }
    }

    async _onOrganizationChanged() {
        await this.render();
    }

    _setIsEmpty(state) {
        this._slickTableContainer.classList.toggle("empty", !!state);
    }

    checkIsEmpty() {
        const gridData = this.model.slickData;
        this._setIsEmpty(!gridData.length);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}
module.exports = ManageConnectionsRenderer;
